import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty, map } from "lodash";
import { FiRepeat } from "react-icons/fi";
import { getBillingDetails } from "actions/adminActions";
import { adminConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";

import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
// BillingForm's own-props are tighter than this call site: its onSuccess
// receives the createInvoiceForCompany mutation result (not BillingDetails) and
// companyId/billingContactEmail are required there — cast at the boundary.
import BillingFormUntyped from "./BillingForm";
import Alert from "components/Alert";
import Button from "components/Button";
import { ISOLVED_WHOLESALE, LEGACY_NOBILL } from "statics/billingTypes";
import ChangeBillingTypeForm from "pages/dashboards/adminDashboard/employers/ChangeBillingTypeForm";
// UpdateBillingContactForm's onSuccess is typed (data?: unknown) => void; this
// call site narrows it to (billingDetails: BillingDetails) => void — cast at
// the boundary.
import UpdateBillingContactFormUntyped from "pages/dashboards/adminDashboard/employers/UpdateBillingContactForm";
import InfoTable from "components/InfoTable";
import IconTable from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import { formatCurrency } from "utils/number";
import type { IconTableColumn } from "components/IconTable";
import type { GetBillingDetailsForCompanyQuery } from "types/graphql";
import type { RootState } from "types/state";

const BillingForm = BillingFormUntyped as React.ComponentType<{
  onClose: () => void;
  onSuccess: (billingDetails: BillingDetails) => void;
  companyId?: string;
  billingContactEmail?: string;
}>;

const UpdateBillingContactForm =
  UpdateBillingContactFormUntyped as React.ComponentType<{
    companyId?: string;
    billingContactEmail?: string;
    onSuccess: (billingDetails: BillingDetails) => void;
    onClose: () => void;
  }>;

/**
 * Billing details payload as returned by getBillingDetails (loosely typed —
 * the source query type is nullable, and state.billingDetails is also set to
 * the empty object `{}` and to form-callback values). The index signature keeps
 * it assignable to InfoTable's `data?: Record<string, unknown>` while typing
 * the two fields actually read in this file.
 */
interface BillingDetails {
  billingContactEmail?: string;
  subscriptionId?: string;
  subscriptions?: BillingSubscription[] | null;
  scheduledSubscriptions?: ScheduledBillingSubscription[] | null;
  [key: string]: unknown;
}

type BillingDetailsQueryResult = NonNullable<
  GetBillingDetailsForCompanyQuery["getBillingDetailsForCompany"]
>;
type BillingSubscription = NonNullable<
  BillingDetailsQueryResult["subscriptions"]
>[number];
type ScheduledBillingSubscription = NonNullable<
  BillingDetailsQueryResult["scheduledSubscriptions"]
>[number];
type BillingPrice = NonNullable<BillingSubscription["prices"]>[number];

const COLLECTION_METHOD_LABELS: Record<string, string> = {
  send_invoice: "Emailed invoice",
  charge_automatically: "Charged automatically",
};

const formatPrices = (prices?: BillingPrice[] | null) =>
  map(prices, (price) => {
    const amount = price.amount === null ? "" : formatCurrency(price.amount);
    const per = price.interval ? ` / ${price.interval}` : "";
    return `${price.lookupKey || price.priceId}${amount ? ` (${amount}${per})` : ""}`;
  }).join(", ");

const formatCollectionMethod = (method?: string | null) =>
  (method && COLLECTION_METHOD_LABELS[method]) || method || "";

const subscriptionColumns: Array<IconTableColumn<BillingSubscription>> = [
  { label: "Subscription ID", key: "subscriptionId" },
  { label: "Status", key: "status" },
  {
    label: "Price",
    customComponent: (props) => <span>{formatPrices(props.prices)}</span>,
  },
  {
    label: "Collection",
    customComponent: (props) => (
      <span>{formatCollectionMethod(props.collectionMethod)}</span>
    ),
  },
  { label: "Current Period Start", key: "currentPeriodStart" },
  { label: "Current Period End", key: "currentPeriodEnd" },
  { label: "Ended", key: "endedAt" },
];

const scheduledColumns: Array<IconTableColumn<ScheduledBillingSubscription>> = [
  { label: "Schedule ID", key: "scheduleId" },
  { label: "Starts", key: "startDate" },
  {
    label: "Price",
    customComponent: (props) => <span>{formatPrices(props.prices)}</span>,
  },
  {
    label: "Setup Fee",
    customComponent: (props) => <span>{formatPrices(props.setupFees)}</span>,
  },
  { label: "Coupon", key: "coupon" },
  {
    label: "Collection",
    customComponent: (props) => (
      <span>{formatCollectionMethod(props.collectionMethod)}</span>
    ),
  },
];

// a customer whose only subscription hasn't started yet, e.g. an order form with deferred billing
const scheduledOnlyMetadata = [
  { key: "billingContactEmail", label: "Billing Contact Email" },
  { key: "stripeCustomerId", label: "Stripe ID" },
];

const metadata = [
  {
    key: "billingContactEmail",
    label: "Billing Contact",
  },
  {
    key: "monthlyFee",
    label: "Subscription Fee",
    format: "currency",
  },
  {
    key: "billingFrequency",
    label: "Billing Frequency",
    format: (val: unknown) => {
      if (val === "year") return "Annually";
      if (val === "month") return "Monthly";
    },
  },
  {
    key: "perUserFee",
    label: "Per User Fee",
    format: "currency",
  },
  {
    key: "nextBillingDate",
    label: "Next Billing Date",
  },
  {
    key: "stripeCustomerId",
    label: "Stripe ID",
  },
  {
    key: "metadata.billedInArrears",
    label: "Billed In Arrears",
    format: (isBilledInArrears: unknown) => (isBilledInArrears ? "Yes" : "No"),
  },
];

const errorSelector = createErrorSelector(adminConstants.GET_BILLING_DETAILS);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getBillingDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  companyAdminEmail?: string;
  // optional per PropTypes, but invoked unconditionally (see render) — kept
  // optional with the `!` house pattern to preserve throw-on-undefined.
  updateCompany?: (update: { billingType: string }) => void;
  billingType?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  showBillingForm: boolean;
  initialFetching: boolean;
  billingDetails: BillingDetails;
  showBillingContactForm: boolean;
  // written only via setState (not in initial state) — undefined on first read.
  showChangeBillingForm?: boolean;
}

class Billing extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showBillingForm: false,
      initialFetching: true,
      billingDetails: {},
      showBillingContactForm: false,
    };
  }

  async componentDidMount() {
    // dispatchFullLifecycle resolves to the SUCCESS or FAILURE action object;
    // `data` is present on SUCCESS. Read it loosely to match the JS behavior.
    const { data } = (await this.props.getBillingDetails(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    )) as { data?: BillingDetails };

    this.setState({
      initialFetching: false,
      billingDetails: data as BillingDetails,
    });
  }

  _getBillingForm = () => {
    const billingContactEmail = get(
      this.state.billingDetails,
      "billingContactEmail",
      ""
    );
    return (
      <BillingForm
        onClose={() => this.setState({ showBillingForm: false })}
        onSuccess={(billingDetails) =>
          this.setState({ showBillingForm: false, billingDetails })
        }
        companyId={this.props.companyId}
        billingContactEmail={
          billingContactEmail || this.props.companyAdminEmail
        }
      />
    );
  };

  _getBillingContactForm = () => {
    return (
      <UpdateBillingContactForm
        companyId={this.props.companyId}
        billingContactEmail={get(
          this.state.billingDetails,
          "billingContactEmail",
          ""
        )}
        // the contact update returns billing details without the subscription lists, keep the ones already loaded
        onSuccess={(billingDetails) =>
          this.setState((state) => ({
            billingDetails: { ...state.billingDetails, ...billingDetails },
            showBillingContactForm: false,
          }))
        }
        onClose={() => this.setState({ showBillingContactForm: false })}
      />
    );
  };

  _getContent() {
    if (this.state.showBillingForm) {
      return this._getBillingForm();
    }

    if (this.state.showBillingContactForm) {
      return this._getBillingContactForm();
    }

    if (this.state.showChangeBillingForm) {
      return (
        <ChangeBillingTypeForm
          companyId={this.props.companyId}
          billingType={this.props.billingType}
          onCancel={() => this.setState({ showChangeBillingForm: false })}
          onSuccess={(billingType) => {
            // optional callback invoked unconditionally — `!` preserves the
            // original throw-on-undefined behavior.
            this.props.updateCompany!({ billingType });
            this.setState({
              showChangeBillingForm: false,
              billingDetails: {},
            });
          }}
        />
      );
    }

    // without billing details there's no telling whether billing exists, only the error alert renders
    if (this.props.error) {
      return null;
    }

    switch (this.props.billingType) {
      case ISOLVED_WHOLESALE:
        return (
          <p className={"page-title centered"}>
            Employer is billed through Isolved Wholesale
          </p>
        );
      case LEGACY_NOBILL:
        return (
          <p className={"page-title centered"}>
            Employer is on a grandfathered non-billable plan
          </p>
        );
    }

    const hasBillingSubscription =
      !isEmpty(this.state.billingDetails) &&
      this.state.billingDetails.subscriptionId;
    // billingDetails is undefined when loading it failed, the error alert renders below
    const subscriptions = get(this.state.billingDetails, "subscriptions") || [];
    const scheduledSubscriptions =
      get(this.state.billingDetails, "scheduledSubscriptions") || [];

    // a scheduled subscription is already billing set up, offering "Setup Billing" would create a second one
    if (hasBillingSubscription || !isEmpty(scheduledSubscriptions)) {
      return (
        <>
          <InfoTable
            metadata={hasBillingSubscription ? metadata : scheduledOnlyMetadata}
            data={this.state.billingDetails}
          />
          {!isEmpty(scheduledSubscriptions) && (
            <>
              <IconTableHeader
                tableHeader="Scheduled Subscriptions"
                tableCount={scheduledSubscriptions.length}
              />
              <IconTable
                columns={scheduledColumns}
                data={scheduledSubscriptions}
              />
            </>
          )}
          {!isEmpty(subscriptions) && (
            <>
              <IconTableHeader
                tableHeader="Subscriptions"
                tableCount={subscriptions.length}
              />
              <IconTable columns={subscriptionColumns} data={subscriptions} />
            </>
          )}
        </>
      );
    }

    return (
      <div className="blank-billing-slate">
        <div>
          <IconMessageCard
            header={"Setup Billing"}
            subheader="It looks like billing needs to be setup for this customer."
            icon={<FiRepeat color="white" stroke="#60A4BF" size={16} />}
            actionText={"Setup Billing"}
            onClick={() => this.setState({ showBillingForm: true })}
          />
        </div>
      </div>
    );
  }

  render() {
    if (this.state.initialFetching) return <IconSpinner centered />;
    const showChangeBillingBtn =
      !this.state.showBillingForm &&
      !this.state.showChangeBillingForm &&
      !this.state.showBillingContactForm;
    const content = this._getContent();
    const hasBillingSubscription =
      !isEmpty(this.state.billingDetails) &&
      (this.state.billingDetails.subscriptionId ||
        !isEmpty(get(this.state.billingDetails, "scheduledSubscriptions")));

    return (
      <div id="admin-billing">
        {content}
        {showChangeBillingBtn && (
          <div className="submit-row btn-row" style={{ float: "right" }}>
            {hasBillingSubscription && (
              <Button
                name="action"
                btnLabel={"Change Billing Contact"}
                color={"primary"}
                size={"sm"}
                onClick={() => this.setState({ showBillingContactForm: true })}
              />
            )}
            <Button
              name="action"
              btnLabel={"Change Billing Type"}
              color={"action"}
              size={"sm"}
              onClick={() => this.setState({ showChangeBillingForm: true })}
            />
          </div>
        )}
        {this.props.error && <Alert type="error" msg={this.props.error} />}
      </div>
    );
  }
}

export default connector(withApollo<Props>(Billing));
